// src/pages/statistics/CustomerStatisticsPage.jsx
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import { FaArrowLeft, FaChartLine, FaRetweet, FaSort, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import StatCard from '../../components/statistic/StatCard';
import { Tooltip } from 'react-tooltip';
import { getSegmentColor } from '../../utils/segmentColors';

const CustomerStatisticsPage = () => {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('segment'); // 'segment', 'lastVisit', 'name'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [visitFilter, setVisitFilter] = useState('all'); // 'all', 'recent', 'old'

  // Función helper para obtener la última visita
  const getLastVisit = (cliente) => {
    if (!cliente.tickets || cliente.tickets.length === 0) {
      return null;
    }

    // Verificar si los tickets son objetos completos o solo IDs
    const firstTicket = cliente.tickets[0];

    // Si los tickets son solo IDs (strings), no podemos obtener la fecha
    if (typeof firstTicket === 'string') {
      return null;
    }

    const sortedTickets = [...cliente.tickets].sort((a, b) => {
      const dateA = a.fecha || a.fechaCreacion || a.createdAt || a.created_at;
      const dateB = b.fecha || b.fechaCreacion || b.createdAt || b.created_at;
      return new Date(dateB) - new Date(dateA);
    });

    const lastTicket = sortedTickets[0];
    return lastTicket;
  };

  // Función helper para obtener el segmento principal
  const getPrimarySegment = (cliente) => {
    if (!cliente.segments || cliente.segments.length === 0) {
      return 'Sin segmento';
    }

    // Priorizar segmentos RFM si existen
    const rfmSegments = cliente.segments.filter(seg =>
      ['Champions', 'Loyal Customers', 'At Risk', 'Can\'t Lose', 'About to Sleep', 'Lost'].includes(seg.label || seg.value)
    );

    if (rfmSegments.length > 0) {
      return rfmSegments[0].label || rfmSegments[0].value;
    }

    return cliente.segments[0].label || cliente.segments[0].value;
  };

  // Función para ordenar clientes
  const sortClientes = (clientesList) => {
    return [...clientesList].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'segment':
          aValue = getPrimarySegment(a);
          bValue = getPrimarySegment(b);
          break;
        case 'lastVisit':
          const aLastVisit = getLastVisit(a);
          const bLastVisit = getLastVisit(b);
          const aDateString = aLastVisit ? (aLastVisit.fecha || aLastVisit.fechaCreacion || aLastVisit.createdAt || aLastVisit.created_at) : null;
          const bDateString = bLastVisit ? (bLastVisit.fecha || bLastVisit.fechaCreacion || bLastVisit.createdAt || bLastVisit.created_at) : null;
          aValue = aDateString ? new Date(aDateString) : new Date(0);
          bValue = bDateString ? new Date(bDateString) : new Date(0);
          break;
        case 'name':
          aValue = `${a.nombre} ${a.apellido}`.toLowerCase();
          bValue = `${b.nombre} ${b.apellido}`.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Función para filtrar clientes
  const filterClientes = (clientesList) => {
    let filtered = [...clientesList];

    // Filtro por segmento
    if (segmentFilter !== 'all') {
      filtered = filtered.filter(cliente => {
        const primarySegment = getPrimarySegment(cliente);
        return primarySegment === segmentFilter;
      });
    }

    // Filtro por fecha de visita
    if (visitFilter !== 'all') {
      filtered = filtered.filter(cliente => {
        const lastVisit = getLastVisit(cliente);
        if (!lastVisit) return visitFilter === 'old';

        const visitDateString = lastVisit.fecha || lastVisit.fechaCreacion || lastVisit.createdAt || lastVisit.created_at;
        const visitDate = new Date(visitDateString);
        const now = new Date();
        const diffTime = Math.abs(now - visitDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (visitFilter === 'recent') {
          return diffDays <= 30; // Últimos 30 días
        } else if (visitFilter === 'old') {
          return diffDays > 30; // Más de 30 días
        }

        return true;
      });
    }

    return filtered;
  };

  // Efecto para aplicar filtros y ordenamiento
  useEffect(() => {
    if (clientes.length > 0) {
      const filtered = filterClientes(clientes);
      const sorted = sortClientes(filtered);
      setFilteredClientes(sorted);
    }
  }, [clientes, sortBy, sortOrder, segmentFilter, visitFilter]);

  useEffect(() => {
    if (clienteId) {
      const fetchData = async () => {
        try {
          setLoading(true);
          // Obtener estadísticas del cliente
          const statsResponse = await get(`customer-statistics/cliente/${clienteId}`);
          setStats(statsResponse);

          // Obtener información básica del cliente
          const clientResponse = await get(`clientes/${clienteId}`);

          // Verificar si necesitamos obtener tickets completos
          if (clientResponse.tickets && clientResponse.tickets.length > 0) {
            const firstTicket = clientResponse.tickets[0];

            if (typeof firstTicket === 'string') {
              // Obtener tickets completos
              const ticketsCompletos = await Promise.all(
                clientResponse.tickets.map(async (ticketId) => {
                  try {
                    const ticketCompleto = await get(`tickets/${ticketId}`);
                    return ticketCompleto;
                  } catch (error) {
                    return null;
                  }
                })
              );
              const clienteConTicketsCompletos = {
                ...clientResponse,
                tickets: ticketsCompletos.filter(ticket => ticket !== null)
              };
              setCliente(clienteConTicketsCompletos);
            } else {
              setCliente(clientResponse);
            }
          } else {
            setCliente(clientResponse);
          }
        } catch (error) {
          setError('No hay suficientes datos para mostrar estadísticas.');
          console.error('Error fetching statistics:', error);
          Swal.fire({
            title: 'Error',
            text: error.message || 'No hay suficientes datos para mostrar estadísticas.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else {
      // Si no hay clienteId, cargar lista de clientes de la marca
      const fetchClientes = async () => {
        try {
          setLoading(true);
          // Primero obtener las marcas del usuario
          const marcasResponse = await get('marcas');
          if (marcasResponse && marcasResponse.length > 0) {
            // Usar la primera marca para obtener sus clientes
            const marcaId = marcasResponse[0]._id;
            const response = await get(`marcas/${marcaId}/clientes`);
            if (response && response.length > 0) {
              // Verificar si los tickets son solo IDs y necesitamos obtener los tickets completos
              const firstClient = response[0];
              if (firstClient.tickets && firstClient.tickets.length > 0) {
                const firstTicket = firstClient.tickets[0];

                if (typeof firstTicket === 'string') {
                  // Los tickets son solo IDs, necesitamos obtener los tickets completos
                  const clientesConTicketsCompletos = await Promise.all(
                    response.map(async (cliente) => {
                      try {
                        const ticketsCompletos = await Promise.all(
                          cliente.tickets.map(async (ticketId) => {
                            try {
                              const ticketCompleto = await get(`tickets/${ticketId}`);
                              return ticketCompleto;
                            } catch (error) {
                              return null;
                            }
                          })
                        );
                        return {
                          ...cliente,
                          tickets: ticketsCompletos.filter(ticket => ticket !== null)
                        };
                      } catch (error) {
                        return cliente;
                      }
                    })
                  );
                  setClientes(clientesConTicketsCompletos);
                } else {
                  setClientes(response);
                }
              } else {
                setClientes(response);
              }
            } else {
              setClientes(response);
            }
          } else {
            setError('No se encontraron marcas para este usuario.');
          }
        } catch (error) {
          setError('No se pudieron cargar los clientes de la marca.');
          console.error('Error fetching clientes:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchClientes();
    }
  }, [clienteId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleClienteSelect = (selectedClienteId) => {
    navigate(`/statistics/customer/${selectedClienteId}`);
  };

  if (error) {
    return <div className="p-6 text-red-500">{error}</div>;
  }

  if (loading) {
    return <div className="p-6">Cargando...</div>;
  }

  // Si no hay clienteId, mostrar lista de clientes
  if (!clienteId) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-6">
        <div className="container mx-auto">
          <button
            onClick={handleBack}
            className="mb-4 flex items-center text-blue-600 hover:underline"
          >
            <FaArrowLeft className="mr-2" /> Volver
          </button>
          <h1 className="text-2xl md:text-4xl font-bold mb-6 text-center text-gray-800">
            Seleccionar Cliente para Estadísticas
          </h1>

          {/* Contador de clientes */}
          <div className="mb-6 text-center">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 inline-block">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{filteredClientes.length}</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-900">Clientes Mostrados</p>
                  <p className="text-xs text-blue-700">
                    {filteredClientes.length === clientes.length
                      ? 'Todos los clientes'
                      : `${filteredClientes.length} de ${clientes.length} clientes`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros y ordenamiento */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Ordenamiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaSort className="inline mr-1" />
                  Ordenar por
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="segment">Segmento</option>
                  <option value="lastVisit">Última Visita</option>
                  <option value="name">Nombre</option>
                </select>
              </div>

              {/* Orden */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orden
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="asc">Ascendente</option>
                  <option value="desc">Descendente</option>
                </select>
              </div>

              {/* Filtro por visita */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaFilter className="inline mr-1" />
                  Filtro por Visita
                </label>
                <select
                  value={visitFilter}
                  onChange={(e) => setVisitFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Todas las visitas</option>
                  <option value="recent">Últimos 30 días</option>
                  <option value="old">Más de 30 días</option>
                </select>
              </div>
            </div>

            {/* Filtro por segmento */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtro por Segmento
              </label>
              <select
                value={segmentFilter}
                onChange={(e) => setSegmentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los segmentos</option>
                <option value="Champions">Champions</option>
                <option value="Loyal Customers">Loyal Customers</option>
                <option value="At Risk">At Risk</option>
                <option value="Can't Lose">Can't Lose</option>
                <option value="About to Sleep">About to Sleep</option>
                <option value="Lost">Lost</option>
                <option value="VIP">VIP</option>
                <option value="Frecuentes">Frecuentes</option>
                <option value="Inactivos">Inactivos</option>
                <option value="Sin segmento">Sin segmento</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClientes.map((cliente) => (
              <div
                key={cliente._id}
                onClick={() => handleClienteSelect(cliente._id)}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer border-l-4"
                style={{
                  borderLeftColor: (() => {
                    const segment = getPrimarySegment(cliente);
                    const colorMap = {
                      'Champions': '#10B981',
                      'Loyal Customers': '#3B82F6',
                      'At Risk': '#F59E0B',
                      'Can\'t Lose': '#EF4444',
                      'About to Sleep': '#F97316',
                      'Lost': '#6B7280',
                      'VIP': '#10B981',
                      'Frecuentes': '#F59E0B',
                      'Inactivos': '#EF4444',
                      'Sin segmento': '#9CA3AF'
                    };
                    return colorMap[segment] || '#9CA3AF';
                  })()
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {cliente.nombre} {cliente.apellido}
                    </h3>
                    <p className="text-gray-600 text-sm">{cliente.email}</p>
                  </div>
                  <div className="ml-2">
                    <span className={`inline-block text-xs px-2 py-1 rounded ${getSegmentColor(getPrimarySegment(cliente))}`}>
                      {getPrimarySegment(cliente)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">Última visita:</span>
                    {(() => {
                      const lastVisit = getLastVisit(cliente);
                      if (!lastVisit) {
                        return <span className="text-red-500">Sin visitas</span>;
                      }

                      // Obtener la fecha del ticket (puede estar en diferentes propiedades)
                      const visitDateString = lastVisit.fecha || lastVisit.fechaCreacion || lastVisit.createdAt || lastVisit.created_at;

                      if (!visitDateString) {
                        return <span className="text-orange-500">Sin fecha</span>;
                      }

                      const visitDate = new Date(visitDateString);
                      const now = new Date();

                      if (isNaN(visitDate.getTime())) {
                        return <span className="text-red-500">Fecha inválida</span>;
                      }

                      const diffTime = Math.abs(now - visitDate);
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                      let colorClass = 'text-gray-600';
                      if (diffDays <= 7) {
                        colorClass = 'text-green-600 font-medium';
                      } else if (diffDays <= 30) {
                        colorClass = 'text-yellow-600';
                      } else {
                        colorClass = 'text-red-600';
                      }

                      return (
                        <span className={colorClass}>
                          {visitDate.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })} ({diffDays === 1 ? 'Ayer' :
                            diffDays < 7 ? `Hace ${diffDays} días` :
                              diffDays < 30 ? `Hace ${Math.floor(diffDays / 7)} semanas` :
                                `Hace ${Math.floor(diffDays / 30)} meses`})
                        </span>
                      );
                    })()}
                  </div>

                  {cliente.rfmScore && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">RFM:</span>
                      <span className="font-bold text-blue-600">{cliente.rfmScore}</span>
                    </div>
                  )}

                  {cliente.avg_basket_size && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Ticket promedio:</span>
                      <span className="font-bold text-green-600">${cliente.avg_basket_size.toFixed(0)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div className="p-6">Cargando estadísticas...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="container mx-auto">
        <button
          onClick={handleBack}
          className="mb-4 flex items-center text-blue-600 hover:underline"
        >
          <FaArrowLeft className="mr-2" /> Volver
        </button>
        <h1 className="text-2xl md:text-4xl font-bold mb-4 text-center text-gray-800">
          Estadísticas del Cliente
        </h1>
        {cliente && (
          <div className="mb-6 text-center">
            <h2 className="text-xl font-semibold">{cliente.nombre} {cliente.apellido}</h2>
            <p className="text-gray-600">{cliente.email}</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <StatCard
            icon={<FaChartLine />}
            title="Total de Puntos"
            value={stats.totalPuntos}
            tooltip="Total de puntos acumulados por el cliente."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Total de Canjes"
            value={stats.totalCanjes}
            tooltip="Número total de puntos canjeados por el cliente."
          />
          <StatCard
            icon={<FaChartLine />}
            title="Valor Promedio de Transacción"
            value={stats.averageTransactionValue ? stats.averageTransactionValue.toFixed(2) : 'N/D'}
            tooltip="Promedio del valor de cada transacción realizada por el cliente."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Frecuencia de Visitas Diaria"
            value={stats.visitFrequency?.daily ? stats.visitFrequency.daily.toFixed(2) : 'N/D'}
            tooltip="Promedio de visitas diarias del cliente."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Frecuencia de Visitas Semanal"
            value={stats.visitFrequency?.weekly ? stats.visitFrequency.weekly.toFixed(2) : 'N/D'}
            tooltip="Promedio de visitas semanales del cliente."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Frecuencia de Visitas Mensual"
            value={stats.visitFrequency?.monthly ? stats.visitFrequency.monthly.toFixed(2) : 'N/D'}
            tooltip="Promedio de visitas mensuales del cliente."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Total de Visitas"
            value={stats.totalVisits}
            tooltip="Número total de visitas realizadas por el cliente."
          />
          {cliente && (() => {
            const lastVisit = getLastVisit(cliente);
            if (!lastVisit) {
              return (
                <StatCard
                  icon={<FaCalendarAlt />}
                  title="Última Compra"
                  value="Sin compras"
                  tooltip="El cliente no ha realizado ninguna compra."
                />
              );
            }

            const visitDateString = lastVisit.fecha || lastVisit.fechaCreacion || lastVisit.createdAt || lastVisit.created_at;
            if (!visitDateString) {
              return (
                <StatCard
                  icon={<FaCalendarAlt />}
                  title="Última Compra"
                  value="Sin fecha"
                  tooltip="No se pudo determinar la fecha de la última compra."
                />
              );
            }

            const visitDate = new Date(visitDateString);
            const now = new Date();
            const diffTime = Math.abs(now - visitDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            let timeAgo = '';
            if (diffDays === 1) {
              timeAgo = 'Ayer';
            } else if (diffDays < 7) {
              timeAgo = `Hace ${diffDays} días`;
            } else if (diffDays < 30) {
              timeAgo = `Hace ${Math.floor(diffDays / 7)} semanas`;
            } else {
              timeAgo = `Hace ${Math.floor(diffDays / 30)} meses`;
            }

            return (
              <StatCard
                icon={<FaCalendarAlt />}
                title="Última Compra"
                value={timeAgo}
                tooltip={`Última compra realizada el ${visitDate.toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}`}
              />
            );
          })()}
        </div>
        <Tooltip id="tooltip" />
      </div>
    </div>
  );
};

export default CustomerStatisticsPage;
